import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImageBackground, StyleSheet, TouchableHighlight, View } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { ScrollingPageView } from '@components';
import { useRecoilState } from 'recoil';

import { fullscreenVODPageTestIDs, onDemandPageTestIDs, pictureDetailedVODPageTestIDs, Theme } from '@constants';
import { useContextReference, useIndexedStateReference, useNodeHandleFromIndex } from '@hooks';
import { navigationService } from '@services';
import { screenReaderEnabledState } from '@store';

import { isAndroid, isTizen, isWeb } from '../../../helpers/Platform';
import { FOCUS_NOW } from '../../../helpers/Tools';
import { getVODDetails } from '../../../hooks/api/useGetVODShowDetails';
import { TWCButton } from '../../focusable/TWCButton/TWCButton';
import { TWCPager } from '../../focusable/TWCPager/TWCPager';
import { TWCText } from '../../primitives/TWCText/TWCText';

const tPath = 'onDemandPage.';

const PagerComponent = isAndroid ? ScrollingPageView : TWCPager;

const CONSTANTS = {
  promoTopSpacing: Theme.px(320),
  promoMarginSpacing: Theme.px(90),
  promoDescriptionWidth: Theme.px(805),
  promoPageIndicatorSize: Theme.px(18),
  promoPageIndicatorTopSpacing: Theme.px(785),
  promoAutoscrollIntervalMS: 7000,
  promoMaxDescriptionLines: 4,
};

/**
 * Component to render as background for VOD promos.
 * Separated out the image backgrounds since they are below the stationary
 * linear gradients, and there is no good way to have a pager view slide that has
 * a stationary separate component between its background and foreground.
 */
const VODPromoBackground = forwardRef(({ promos }, reference) => (
  <>
    <View style={styles.backgroundGradientWrapper}>
      <LinearGradient
        angle={270}
        colors={[Theme.gradients.vodBackground.start, Theme.gradients.vodBackground.end]}
        style={styles.linearGradient}
        useAngle
      >
        <LinearGradient
          colors={[
            Theme.gradients.vodBackground.start,
            Theme.gradients.vodBackground.start,
            Theme.gradients.vodBackground.end,
          ]}
          end={{ x: 0.5, y: 1 }}
          locations={[0, 0.5, 1]}
          start={{ x: 0.5, y: 0 }}
          style={styles.linearGradient}
        />
      </LinearGradient>
    </View>
    <View style={styles.backgroundWrapper}>
      <PagerComponent initialIndex={0} ref={reference}>
        {promos.map((promo, _index) => (
          <View key={`${_index}_background`} collapsable={false} style={styles.detailsViewWrapper}>
            <ImageBackground
              resizeMode={'cover'}
              source={{ uri: promo.wallpaper }}
              style={styles.promoBackground}
              testID={`${onDemandPageTestIDs.promoBackground}${_index}`}
            />
          </View>
        ))}
      </PagerComponent>
    </View>
  </>
));

/**
 * Foreground for VOD promo, containing title, description, and button
 */
const VODPromo = forwardRef(
  (
    {
      promo,
      continueWatching,
      nextFocusLeft,
      nextFocusRight,
      nextFocusDown,
      nextFocusUp,
      testID,
      onFocus,
      onBlur,
      index = 0,
      count = 1,
    },
    reference,
  ) => {
    const { t } = useTranslation();

    const onPress = async () => {
      if (promo?.channel_type === 'video') {
        // Get video id from channel request
        const vodData = await getVODDetails(promo._id, false);

        // Search for continue watching item
        const cwItem = continueWatching.find(item => item._id === vodData.videoID);

        // new data based on continue watching data or vodData
        const newData = cwItem ?? {
          _id: vodData.videoID,
          channel: { _id: null },
          point: null,
        };

        navigationService.navigate(fullscreenVODPageTestIDs.fullscreenVODPage, {
          videoID: newData._id,
          seasonID: newData?.channel?._id,
          needsBookmarking: true,
          startPosition: newData.point,
        });
      } else {
        navigationService.navigate(pictureDetailedVODPageTestIDs.pictureDetailedVODPage, {
          showID: promo._id,
          data: promo,
        });
      }
    };

    return (
      <View style={styles.promoDetailsContainer}>
        <TWCText style={styles.promoTitle} testID={`${onDemandPageTestIDs.promoTitle}${index}`} variant={'boldText66'}>
          {promo.title}
        </TWCText>
        <TWCText
          numberOfLines={CONSTANTS.promoMaxDescriptionLines}
          style={styles.promoDescription}
          testID={`${onDemandPageTestIDs.promoDescription}${index}`}
          variant={'regularText30'}
        >
          {promo.description}
        </TWCText>
        <TWCButton
          accessibilityHint={
            count > 1 ? t(`${tPath}promoAccessibilityHint`) : t(`${tPath}promoAccessibilityHintSingle`)
          }
          accessibilityLabel={
            count > 1
              ? t(`${tPath}promoAccessibilityLabel`, {
                  index: `${index + 1}`,
                  count: `${count}`,
                  title: promo.title,
                  description: promo.description,
                })
              : t(`${tPath}promoAccessibilityLabelSingle`, {
                  title: promo.title,
                  description: promo.description,
                })
          }
          nextFocusDown={nextFocusDown}
          nextFocusLeft={nextFocusLeft}
          nextFocusRight={nextFocusRight}
          nextFocusUp={nextFocusUp}
          ref={reference}
          shouldUseContextReference={false}
          style={styles.button}
          testID={testID}
          title={t(`${tPath}promoButton`)}
          onBlur={onBlur}
          onFocus={onFocus}
          onPress={onPress}
        />
      </View>
    );
  },
);

/**
 * Wrapper around the VODPromo component, used for reference handling in the list
 */
const VODPromosListItem = forwardRef(
  (
    {
      references,
      pagerReference,
      backgroundReference,
      index,
      promo,
      continueWatching,
      allPromos,
      nextFocusDown,
      nextFocusUp,
      onFocus,
      onBlur,
    },
    reference,
  ) => {
    const isLast = index === allPromos.length - 1;
    const isFirst = index === 0;

    const thisHandle = useNodeHandleFromIndex(references, index);
    const noFocus = isAndroid ? thisHandle : null;

    const itemNextHandle = useNodeHandleFromIndex(references, isLast ? null : index + 1);

    const itemPreviousHandle = useNodeHandleFromIndex(references, isFirst ? null : index - 1);

    return (
      <VODPromo
        continueWatching={continueWatching}
        count={allPromos.length}
        index={index}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={isFirst ? noFocus : itemPreviousHandle}
        nextFocusRight={isLast ? noFocus : itemNextHandle}
        nextFocusUp={nextFocusUp}
        promo={promo}
        ref={reference}
        testID={`${onDemandPageTestIDs.promoContent}${index}`}
        onBlur={onBlur}
        onFocus={() => {
          onFocus && onFocus();
          if (isWeb) {
            pagerReference.current?.slickGoTo(index);
            backgroundReference.current?.slickGoTo(index);
          } else {
            pagerReference.current?.setPage(index);
            backgroundReference.current?.setPage(index);
          }
        }}
      />
    );
  },
);

/**
 * Simple page indicator. Will show count dots, where index dot is highlighted.
 */
const PageIndicator = ({ index, count }) => {
  const indicators = [];

  for (let _index = 0; _index < count; _index++) {
    indicators.push(
      <View
        key={`promo_index_indicator_${_index}`}
        style={[
          styles.indicatorDot,
          _index === 0 ? styles.firstDot : null,
          _index === index ? styles.focusedDot : null,
        ]}
      />,
    );
  }
  return <View style={styles.pageIndicator}>{indicators}</View>;
};

/**
 * List to display all VOD promos for the VOD home page. Can take a nextFocusDown
 * that can focus the next row in the vod page.
 *
 * Takes the list of promos and renders a full-screen VOD promo instance for each.
 * Will auto-rotate if screen reader is disabled, every 7 seconds.
 */
export const VODPromosList = ({ promos, continueWatching, nextFocusDown, nextFocusUp, onFocus, testID }) => {
  const pagerReference = useRef();
  const backgroundReference = useRef();
  const listReference = useContextReference(testID);
  const [focusIndex, setFocusIndex] = useState(0);
  const [stateReferences, , setStateReferences] = useIndexedStateReference({});

  const [listHasFocus, setListHasFocus] = useState(false);
  const listHandleFocused = useRef(false);
  const lastChange = useRef(new Date());

  const [screenReaderEnabled] = useRecoilState(screenReaderEnabledState);

  // Auto-play effect
  useEffect(() => {
    let timeout = null;
    // If screen reader enabled or list doesn't have focus, do NOT auto-rotate

    if (listHasFocus && !screenReaderEnabled) {
      const now = new Date();
      const time = CONSTANTS.promoAutoscrollIntervalMS - (now.getTime() - lastChange.current.getTime());

      timeout = setTimeout(() => {
        const newIndex = (focusIndex + 1) % promos.length;
        // The onFocus handler will update the list index

        FOCUS_NOW(stateReferences.current[newIndex]);
      }, time);
    }

    return () => {
      if (timeout !== null) {
        clearTimeout(timeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastChange.current, listHasFocus, focusIndex, screenReaderEnabled]);

  // We render the background seperately since the image should animate but not the gradient overlays
  return (
    <>
      <VODPromoBackground index={focusIndex} promos={promos} ref={backgroundReference} />
      <View style={styles.pageWrapper}>
        <PagerComponent initialIndex={0} ref={pagerReference} onIndexChanged={index => setFocusIndex(index)}>
          {promos.map((promo, index) => (
            <View key={index} collapsable={false} style={styles.detailsViewWrapper}>
              <VODPromosListItem
                allPromos={promos}
                backgroundReference={backgroundReference}
                continueWatching={continueWatching}
                index={index}
                nextFocusDown={nextFocusDown}
                nextFocusUp={nextFocusUp}
                pagerReference={pagerReference}
                promo={promo}
                ref={reference => {
                  setStateReferences(reference, index);
                  if (listHandleFocused.current && focusIndex === index) {
                    FOCUS_NOW(reference);
                  }
                }}
                references={stateReferences}
                onBlur={() => {
                  if (index === focusIndex) {
                    setListHasFocus(false);
                  }
                }}
                onFocus={() => {
                  lastChange.current = new Date();
                  setListHasFocus(true);
                }}
              />
            </View>
          ))}
        </PagerComponent>
      </View>
      {
        // Don't show indicator if only 1 promo
        promos.length > 1 && <PageIndicator count={promos.length} index={focusIndex} />
      }

      <TouchableHighlight
        activeOpacity={1}
        nativeID={testID}
        nextFocusDown={null}
        nextFocusLeft={null}
        nextFocusRight={null}
        nextFocusUp={null}
        ref={listReference}
        style={styles.focusNode}
        testID={testID}
        underlayColor={Theme.transparent}
        onBlur={() => (listHandleFocused.current = false)}
        onFocus={() => {
          listHandleFocused.current = true;
          setListHasFocus(true);
          onFocus();
          setTimeout(() => {
            FOCUS_NOW(stateReferences.current[focusIndex]);
          }, 10);
        }}
      >
        <View />
      </TouchableHighlight>
    </>
  );
};

/**
 * Section used on VOD home page for any VOD promos. Wrapper of VODPromosList component.
 */
export const OnDemandPagePromoSection = ({
  promos,
  continueWatching,
  onFocus,
  onBlur,
  testID,
  nextFocusDown,
  nextFocusUp = null,
}) => {
  promos = promos?.[0]?.channels;

  return promos ? (
    <View style={styles.promo}>
      <VODPromosList
        continueWatching={continueWatching}
        nextFocusDown={nextFocusDown}
        nextFocusUp={nextFocusUp}
        promos={promos}
        testID={testID}
        onBlur={onBlur}
        onFocus={onFocus}
      />
    </View>
  ) : null;
};

const styles = StyleSheet.create({
  backgroundGradientWrapper: {
    elevation: 1,
    height: Theme.appHeight,
    left: 0,
    position: isWeb && !isTizen ? 'fixed' : 'absolute',
    top: 0,
    width: Theme.appWidth,
    zIndex: 0,
  },
  backgroundWrapper: {
    elevation: 1,
    height: Theme.appHeight,
    left: 0,
    position: isWeb && !isTizen ? 'fixed' : 'absolute',
    top: 0,
    width: Theme.appWidth,
    zIndex: -1,
  },
  button: {
    marginTop: Theme.px(74),
  },
  detailsViewWrapper: {
    flex: 1,
    height: Theme.appHeight,
    width: Theme.appWidth,
  },
  firstDot: {
    marginLeft: 0,
  },
  focusNode: {
    height: 1,
    width: 1,
  },
  focusedDot: {
    backgroundColor: Theme.vodEnabled,
  },
  indicatorDot: {
    backgroundColor: Theme.vodDisabled,
    borderRadius: CONSTANTS.promoPageIndicatorSize / 2,
    height: CONSTANTS.promoPageIndicatorSize,
    marginLeft: CONSTANTS.promoPageIndicatorSize,
    width: CONSTANTS.promoPageIndicatorSize,
  },
  linearGradient: {
    height: Theme.appHeight,
    width: Theme.appWidth,
  },
  pageIndicator: {
    alignItems: 'center',
    elevation: 2,
    flexDirection: 'row',
    height: CONSTANTS.promoPageIndicatorSize,
    justifyContent: 'center',
    left: 0,
    position: 'absolute',
    top: CONSTANTS.promoPageIndicatorTopSpacing,
    width: Theme.appWidth,
  },
  pageWrapper: {
    elevation: 2,
    height: Theme.appHeight,
    left: 0,
    position: isWeb && !isTizen ? 'fixed' : 'absolute',
    top: 0,
    width: Theme.appWidth,
  },
  promo: {
    flex: 1,
    height: Theme.appHeight,
    width: Theme.appWidth,
  },
  promoBackground: {
    height: Theme.appHeight,
    width: Theme.appWidth,
  },
  promoDescription: {
    width: CONSTANTS.promoDescriptionWidth,
  },
  promoDetailsContainer: {
    flexDirection: 'column',
    marginLeft: CONSTANTS.promoMarginSpacing,
    marginTop: CONSTANTS.promoTopSpacing,
  },
  promoTitle: {
    marginBottom: Theme.px(22),
  },
});
